import React, { useState, useEffect } from 'react';
import FileUpload from '../../components/FileUpload/FileUpload';
import MarkerTable from '../../components/MarkerTable/MarkerTable';
import TrendChart from '../../components/TrendChart/TrendChart';
import {
  uploadBloodAnalysis,
  getBloodAnalysisResults,
  getBloodAnalysisTrends,
  BloodAnalysisResult,
  TrendPoint,
} from '../../utils/api/natureDrApi';
import './BloodAnalysis.css';

type Tab = 'upload' | 'history' | 'trends';

const BloodAnalysis: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('upload');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [currentResult, setCurrentResult] = useState<BloodAnalysisResult | null>(null);

  // History state
  const [results, setResults] = useState<BloodAnalysisResult[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Trends state
  const [trends, setTrends] = useState<TrendPoint[]>([]);
  const [availableMarkers, setAvailableMarkers] = useState<string[]>([]);
  const [selectedMarker, setSelectedMarker] = useState<string>('');
  const [trendsLoading, setTrendsLoading] = useState(false);

  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    setUploadError(null);

    try {
      const result = await uploadBloodAnalysis(file);
      setCurrentResult(result);
    } catch (error: any) {
      setUploadError(error.message || 'Failed to analyse blood test');
    } finally {
      setIsUploading(false);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await getBloodAnalysisResults();
      setResults(response.results);
    } catch (error) {
      // Silent failure - empty state shown
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadTrends = async (marker?: string) => {
    setTrendsLoading(true);
    try {
      const response = await getBloodAnalysisTrends(marker);
      setTrends(response.trends || []);
      const markers = response.availableMarkers || [];
      setAvailableMarkers(markers);
      if (!selectedMarker && markers.length > 0) {
        setSelectedMarker(markers[0]);
      }
    } catch (error) {
      // Silent failure
    } finally {
      setTrendsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    } else if (activeTab === 'trends') {
      loadTrends(selectedMarker || undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleMarkerChange = (marker: string) => {
    setSelectedMarker(marker);
    loadTrends(marker);
  };

  const handleViewResult = (result: BloodAnalysisResult) => {
    setCurrentResult(result);
    setActiveTab('upload');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNewUpload = () => {
    setCurrentResult(null);
    setUploadError(null);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Unknown date';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Find reference ranges for the selected trend marker from latest result
  const getMarkerRefRange = () => {
    if (!selectedMarker || results.length === 0) return { min: null, max: null, unit: '' };
    for (const result of results) {
      const marker = result.markers?.find(m => m.name === selectedMarker);
      if (marker) {
        return { min: marker.referenceMin, max: marker.referenceMax, unit: marker.unit };
      }
    }
    return { min: null, max: null, unit: '' };
  };

  return (
    <div className="blood-analysis-container">
      <div className="blood-analysis-header">
        <div className="blood-header-icon">
          <i className="fa-solid fa-vial" />
        </div>
        <h1>Blood Analysis</h1>
        <p className="blood-subtitle">
          Upload your blood test results and NatureDr will provide personalised insights with natural remedy recommendations
        </p>
      </div>

      <div className="blood-tabs">
        <button
          className={`blood-tab ${activeTab === 'upload' ? 'active' : ''}`}
          onClick={() => setActiveTab('upload')}
        >
          <i className="fa-solid fa-upload" />
          <span>Upload & Results</span>
        </button>
        <button
          className={`blood-tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          <i className="fa-solid fa-clock-rotate-left" />
          <span>History</span>
        </button>
        <button
          className={`blood-tab ${activeTab === 'trends' ? 'active' : ''}`}
          onClick={() => setActiveTab('trends')}
        >
          <i className="fa-solid fa-chart-line" />
          <span>Trends</span>
        </button>
      </div>

      <div className="blood-tab-content">
        {/* UPLOAD & RESULTS TAB */}
        {activeTab === 'upload' && (
          <div className="tab-panel">
            {!currentResult ? (
              <>
                <FileUpload onFileSelect={handleFileUpload} isUploading={isUploading} />
                {isUploading && (
                  <div className="analysis-progress">
                    <div className="progress-steps">
                      <div className="progress-step active">
                        <i className="fa-solid fa-upload" />
                        <span>Uploading</span>
                      </div>
                      <div className="progress-step active">
                        <i className="fa-solid fa-magnifying-glass" />
                        <span>Extracting markers</span>
                      </div>
                      <div className="progress-step">
                        <i className="fa-solid fa-leaf" />
                        <span>Finding remedies</span>
                      </div>
                    </div>
                  </div>
                )}
                {uploadError && (
                  <div className="upload-error">
                    <i className="fa-solid fa-circle-exclamation" />
                    <p>{uploadError}</p>
                  </div>
                )}
              </>
            ) : (
              <div className="result-view">
                <div className="result-header">
                  <div className="result-meta">
                    <h2>Analysis Results</h2>
                    <div className="result-details">
                      {currentResult.labName && (
                        <span className="result-lab">
                          <i className="fa-solid fa-hospital" /> {currentResult.labName}
                        </span>
                      )}
                      {currentResult.testDate && (
                        <span className="result-date">
                          <i className="fa-solid fa-calendar" /> {formatDate(currentResult.testDate)}
                        </span>
                      )}
                      <span className="result-file">
                        <i className="fa-solid fa-file" /> {currentResult.fileName}
                      </span>
                    </div>
                  </div>
                  <button className="new-upload-btn" onClick={handleNewUpload}>
                    <i className="fa-solid fa-plus" /> New Upload
                  </button>
                </div>

                {/* Summary cards */}
                <div className="result-summary">
                  <div className="summary-card total">
                    <span className="summary-number">{currentResult.markers.length}</span>
                    <span className="summary-label">Total Markers</span>
                  </div>
                  <div className="summary-card normal">
                    <span className="summary-number">
                      {currentResult.markers.length - currentResult.abnormalMarkers.length}
                    </span>
                    <span className="summary-label">Normal</span>
                  </div>
                  <div className="summary-card attention">
                    <span className="summary-number">{currentResult.abnormalMarkers.length}</span>
                    <span className="summary-label">Needs Attention</span>
                  </div>
                </div>

                {/* General advice */}
                <div className="general-advice">
                  <i className="fa-solid fa-lightbulb" />
                  <p>{currentResult.generalAdvice}</p>
                </div>

                {/* Markers table */}
                <MarkerTable
                  markers={currentResult.markers}
                  recommendations={currentResult.recommendations}
                />

                {/* Warning */}
                <div className="blood-warning">
                  <i className="fa-solid fa-triangle-exclamation" />
                  <p>{currentResult.warning}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* HISTORY TAB */}
        {activeTab === 'history' && (
          <div className="tab-panel">
            {historyLoading ? (
              <div className="loading-state">
                <span className="loading-spinner" />
                <p>Loading your results...</p>
              </div>
            ) : results.length === 0 ? (
              <div className="empty-history">
                <i className="fa-solid fa-flask-vial" />
                <h3>No results yet</h3>
                <p>Upload your first blood test to get started</p>
                <button className="start-upload-btn" onClick={() => setActiveTab('upload')}>
                  <i className="fa-solid fa-upload" /> Upload Blood Test
                </button>
              </div>
            ) : (
              <div className="history-list">
                {results.map((result, index) => (
                  <div
                    key={result.id}
                    className="history-card"
                    style={{ animationDelay: `${index * 0.08}s` }}
                    onClick={() => handleViewResult(result)}
                  >
                    <div className="history-card-left">
                      <div className="history-date">
                        {formatDate(result.testDate || result.createdAt)}
                      </div>
                      <div className="history-file">{result.fileName}</div>
                      {result.labName && (
                        <div className="history-lab">{result.labName}</div>
                      )}
                    </div>
                    <div className="history-card-right">
                      <div className="history-stats">
                        <span className="history-stat normal">
                          <i className="fa-solid fa-check" />
                          {result.markers.length - result.abnormalMarkers.length}
                        </span>
                        {result.abnormalMarkers.length > 0 && (
                          <span className="history-stat attention">
                            <i className="fa-solid fa-exclamation" />
                            {result.abnormalMarkers.length}
                          </span>
                        )}
                      </div>
                      <i className="fa-solid fa-chevron-right history-arrow" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TRENDS TAB */}
        {activeTab === 'trends' && (
          <div className="tab-panel">
            {trendsLoading ? (
              <div className="loading-state">
                <span className="loading-spinner" />
                <p>Loading trends...</p>
              </div>
            ) : availableMarkers.length === 0 ? (
              <div className="empty-history">
                <i className="fa-solid fa-chart-line" />
                <h3>No trend data yet</h3>
                <p>Upload at least two blood tests to see how your markers change over time</p>
                <button className="start-upload-btn" onClick={() => setActiveTab('upload')}>
                  <i className="fa-solid fa-upload" /> Upload Blood Test
                </button>
              </div>
            ) : (
              <>
                <div className="trends-marker-select">
                  <label htmlFor="marker-select">Select Marker:</label>
                  <select
                    id="marker-select"
                    value={selectedMarker}
                    onChange={(e) => handleMarkerChange(e.target.value)}
                  >
                    {availableMarkers.map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>

                {selectedMarker && (
                  <TrendChart
                    trends={trends}
                    markerName={selectedMarker}
                    referenceMin={getMarkerRefRange().min}
                    referenceMax={getMarkerRefRange().max}
                    unit={getMarkerRefRange().unit}
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BloodAnalysis;
